import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  EventEmitter,
  Output,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatCardModule } from "@angular/material/card";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { CartOverlayEventsService } from "./mini-cart-overlay-events.service";
import { TableService } from "../../common/services/table-service";
import { Quantity } from "../../common/components/general/quantity/quantity";
import { Table } from "../../table/table";
import { Button } from "../../common/components/general/button/button";
import { Icon } from "../../common/components/general/icon/icon";
import { ReusableService } from "../../common/services/reusable-services";
import { MiniCartService } from "./mini-cart.service";
import { MatBadgeModule } from "@angular/material/badge";
import { Label } from "../../common/models/page-components";
import { COMPONENT_META_DATA } from "../../common/constants/component-meta-data";
import { ContentFilterPipe } from "../../common/pipes/content-filter-pipe";
import { HeaderService } from "../header.service";
import { firstValueFrom, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Caption } from "../../common/components/general/caption/caption";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RemoveConfirmationDialog, RemoveConfirmationDialogData } from "../../common/components/general/remove-confirmation-dialog/remove-confirmation-dialog";
import { MatDialog } from '@angular/material/dialog';
import { ErrorData } from "../../common/models/error-data";
import { CacheService } from "../../common/services/cache-service";
import { CACHE_KEYS } from "../../common/constants/cache-keys";
import { UserDetailsPartial } from "../user-profile/user-profile.model";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TextInputComponent } from "../../common/components/general/text-input/text-input";
import { SRP_ROUTES } from "../../common/constants/routes";
import { BreadcrumbService } from "../../common/services/breadcrumb.service";
import { UserBrrContext } from "../../common/models/brr-context";
import { AnalyticsService } from "../../common/services/analytics-service";
import { EstimateListService } from "../../estimate-list/estimate-list-service";
import { EstimateService } from "../../my-estimates/estimate.service";
import { SrpSnackbarService } from "../../common/services/srp-snackbar.service";
type CartItemType = 'parts' | 'labour' | 'operation';

@Component({
  selector: "srp-mini-cart",
  standalone: true,
  imports: [
    MatMenuModule,
    MatIconModule,
    MatExpansionModule,
    MatCardModule,
    CommonModule,
    FormsModule,
    Table,
    Button,
    Icon,
    Quantity,
    MatBadgeModule,
    Caption,
    FormsModule, MatFormFieldModule, MatInputModule, MatTooltipModule,
    ContentFilterPipe, MatProgressSpinner, TextInputComponent
  ],
  providers: [ContentFilterPipe],
  templateUrl: "./mini-cart.html",
  styleUrl: "./mini-cart.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MiniCart implements OnInit, OnDestroy {
  @Input() pageContentLabels: Label[] = [];
  @Input() appContext: string;
  cancel = signal("cancel");
  distributorLength = signal<number>(0);
  private readonly destroy$ = new Subject<void>();
  remove = signal("Remove");
  @Output() errorResponse: EventEmitter<ErrorData> = new EventEmitter<ErrorData>();
  breadcrumbService = inject(BreadcrumbService);
  private readonly contentPipe = inject(ContentFilterPipe);
  private readonly elRef = inject(ElementRef);
  private readonly miniCartService = inject(MiniCartService);
  maxCount = 99;
  cartTitle: string = 'Untitled Cart';
  isEditing: boolean = false;
  subTotal: string = '0.00';
  totalPrice: number = 0;
  partsToolTip: string = "Delete Parts Row";
  operationDelete: string = "Delete Entire Operation"
  labourToolTip: string = 'Delete Labour Row';
  editTitle = signal<boolean>(true);
  titleValue = signal<string>('');
  readonly panelOpenState = signal(false);
  cartItemsCount = signal<number>(0);
  isFrozen = signal<boolean>(false);
  overlayHeadingParts = signal('Remove a row from Parts Table?');
  overlayHeadingLabour = signal('Remove a row from Labour Table?');
  overlayDescriptionParts = signal('Are you sure? you want to delete a row from Parts Table in Mini Cart ?');
  overlayDescriptionLabour = signal('Are you sure? you want to delete a row from Labour Table in Mini Cart ?');
  overlayHeadingOperation = signal("Remove an operation?");
  overlayDescriptionOperation = signal("Are you sure? you want to delete an operation from Mini Cart ?");
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  labels: Label[];
  showSpinner = signal<boolean>(false);
  cartLoaded = signal<boolean>(false);
  @ViewChild('quantityTemplate', { static: true }) quantityTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true }) actionTemplate!: TemplateRef<any>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('descriptionTemplate', { static: true }) descriptionTemplate!: TemplateRef<any>;
  @ViewChild('availabilityTemplate', { static: true }) availabilityTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplateLabour', { static: true }) actionTemplateLabour!: TemplateRef<any>;
  @ViewChild('priceTemplate', { static: true }) priceTemplate!: TemplateRef<any>;

  operationGroups: {
    operationName: string;
    partsTableKey: string;
    labourTableKey: string;
    subTotal: string;
    totalPrice: number;
    partsLength: number;
    labourLength: number
  }[] = [];

  emptyLabour: boolean = true;
  emptyParts: boolean = true;
  selectedRow: boolean;
  selectedType: CartItemType;
  expandedOp: number = 0;
  cartId: string;
  estimateId: string | null = null;
  selectedIndex: number;
  currencySymbol: string;
  accordionIndexes: number[] = [];

  constructor(
    public readonly cartEventService: CartOverlayEventsService,
    public readonly tableService: TableService,
    public readonly reusableService: ReusableService,
    public readonly router: Router,
    public readonly headerService: HeaderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly cacheService: CacheService,
    private readonly dialog: MatDialog,
    private readonly analyticsService: AnalyticsService,
    private readonly estimateListService: EstimateListService,
    private readonly estimateService: EstimateService,
    private readonly srpSnackbarService: SrpSnackbarService
  ) { }

  ngOnInit() {
    this.accordionIndexes.push(0);
    this.cartTitle = this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART) || 'Untitled Cart';

    //If Cart selected , get value from cache and update mini cart count
    this.cartId = this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID);

    let userBrrContext = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_BRR_CONTEXT) as UserBrrContext;

    this.distributorLength.set(userBrrContext?.radprCount ?? 0);
    this.miniCartService.miniCartInfoValue.pipe(
      takeUntil(this.destroy$)
    ).subscribe(response => {
      if (response) {
        this.cartItemsCount.set(response?.cartItemCount?.count ?? 0);
        const cartName = response?.cartItemCount?.cartName;
        this.titleValue.set(cartName || ((this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART)) || 'Untitled Cart'));

        this.cdr.detectChanges();
      }
    });
    this.setUpMiniCartData();
    this.currencySymbol = this.reusableService.getCurrencySymbol();
  }

  showInput = signal<boolean>(false);
  @ViewChild('textInput', { static: false, read: ElementRef }) textInput: ElementRef;
  setTitle(): void {
    if (this.isFrozen()) return;
    this.showInput.set(true);
    setTimeout(() => {
      const input = this.textInput.nativeElement.querySelector('input');
      input.focus();
      input.value = this.titleValue();
    }, 100);
  }

  saveTitleValue(): void {
    if (this.isFrozen()) return;
    this.showInput.set(false);
    const title = this.textInput.nativeElement.querySelector('input').value;
    if (title && title !== this.titleValue()) {
      this.titleValue.set(title);
      this.updateCartName();
    }
  }

  getTitle(event: KeyboardEvent): void {
    if (this.isFrozen()) return;
    event.stopImmediatePropagation();
    if (event.key === 'Enter') {
      const title = (event.target as HTMLInputElement).value;
      if (title && title !== this.titleValue()) {
        this.titleValue.set(title);
        this.updateCartName();
      }
      this.showInput.set(false);
    }
  }

  focusOut(): void {
    this.showInput.set(false);
  }

  toggleAccordion(i: number): void {
    if (this.accordionIndexes.includes(i)) {
      this.accordionIndexes.splice(this.accordionIndexes.indexOf(i), 1);
    } else {
      this.accordionIndexes.push(i);
    }
  }

  isMiniCartDisabled(): boolean {
    return this.cartItemsCount() === 0 || String(this.cartItemsCount()) === '0';
  }

  setUpMiniCartData(): void {
    this.headerService.getHeaderResponseFromObservable().pipe(
      takeUntil(this.destroy$)
    ).subscribe(async response => {
      this.labels = response?.data?.labels;
      const getLabel = (labelKey: string): string => {
        const value = this.contentPipe.transform(this.labels, labelKey);
        return value;
      };

      const headingText = getLabel(this.LABELS.HEADING_TEXT) || "Remove a row from";
      const descriptionText = getLabel(this.LABELS.DESCRIPTION_TEXT) || 'Are you sure, you want to remove';
      const miniCart = getLabel(this.LABELS.MINI_CART) || 'in Mini Cart?';

      this.operationDelete = getLabel(this.LABELS.DELETE_OPERATION_TOOLTIP) || 'Delete Entire Operation';
      this.partsToolTip = getLabel(this.LABELS.REMOVE_PARTS_TOOLTIP) || 'Remove Parts';
      this.labourToolTip = getLabel(this.LABELS.REMOVE_LABOUR_TOOLTIP) || 'Remove Labour';


      const setOverlayTexts = (
        headingSignal: any,
        descriptionSignal: any,
        headingLabel: string,
        descriptionLabel: string
      ): void => {
        const headingValue = getLabel(headingLabel);
        const descriptionValue = getLabel(descriptionLabel);

        headingSignal.set(`${headingText} ${headingValue}`);
        descriptionSignal.set(`${descriptionText} ${descriptionValue} ${miniCart}`);
      };

      [
        {
          headingSignal: this.overlayHeadingParts,
          descriptionSignal: this.overlayDescriptionParts,
          headingLabel: this.LABELS.PARTS_TABLE_HEADING,
          descriptionLabel: this.LABELS.PARTS_TABLE_DESCRIPTION
        },
        {
          headingSignal: this.overlayHeadingOperation,
          descriptionSignal: this.overlayDescriptionOperation,
          headingLabel: this.LABELS.OPERATION_HEADING,
          descriptionLabel: this.LABELS.OPERATION_DESCRIPTION
        },
        {
          headingSignal: this.overlayHeadingLabour,
          descriptionSignal: this.overlayDescriptionLabour,
          headingLabel: this.LABELS.LABOUR_TABLE_HEADING,
          descriptionLabel: this.LABELS.LABOUR_TABLE_DESCRIPTION
        }
      ].forEach(item =>
        setOverlayTexts(item.headingSignal, item.descriptionSignal, item.headingLabel, item.descriptionLabel)
      );


    });
  }

  onMenuClick() {
    this.cartId = this.cacheService.getFromCache(CACHE_KEYS.USER.CART_ID);

    // Track GTM event immediately when user clicks to ensure correct event order and timing
    this.analyticsService.trackVirtualPathUpdate('/SRP/shopping-cart-summary', 'shopping cart summary page');

    this.loadCartData();
  }

  async loadCartData() {
    this.showSpinner.set(true);
    // Clear previous cart data immediately to prevent showing stale data
    this.operationGroups = [];
    this.totalPrice = 0;
    this.cdr.detectChanges();

    try {
      const response = await this.miniCartService.getCart(this.cartId, true);
      if (response?.data) {
        const cart = response.data?.getCartById?.cart;
        // Setting mini cart count directly from getCart, removing getCartItemCount call
        const cartItemsCount = response?.data?.getCartById?.cart?.cartItemsCount ?? 0;
        this.cartItemsCount.set(cartItemsCount);
        this.miniCartService.updateMiniCartInfo({cartItemCount: {
          cartName: cart?.cartName,
          count: cartItemsCount,
        }});
        this.estimateId = (cart as { estimateId?: string } | null)?.estimateId ?? null;
        this.isFrozen.set(cart?.isFrozen === '1');
        if (this.isFrozen()) {
          this.showInput.set(false);
        }
        this.cartTitle = cart.cartName ? cart.cartName : ((this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART)) || 'Untitled Cart');

        this.titleValue.set(this.cartTitle);

        this.groupOperations(cart);
        this.miniCartService.setCartData(cart);
      }
    } catch (error) {
      // Keep empty state hidden until load completes
      console.error('Failed to load cart data:', error);
    } finally {
      this.showSpinner.set(false);
      this.cartLoaded.set(true);
    }

  }

  @ViewChild('titleInput', { static: false }) titleInput: ElementRef;

  private updateCartName() {
    if (this.isFrozen()) return;
    const request: { cartId: string, cartName: string, itemId: string | null, qty: number | null } = {
      cartId: this.cartId,
      cartName: this.titleValue(),
      itemId: null,
      qty: null,
    };
    this.miniCartService.updateCart(request).subscribe((response) => {
      this.titleValue.set(request.cartName ? request.cartName : ((this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART)) || 'Untitled Cart'));
      const cartData = response.data?.updateProductsToCart?.cart ?? { cartItems: [] };
      this.miniCartService.setCartData(cartData);
      // Derive total count from response instead of calling count API
      const totalCount = (cartData?.cartItems ?? []).reduce((acc: number, op: any) => {
        const partsCount = Array.isArray(op?.parts) ? op.parts.length : 0;
        const labourCount = Array.isArray(op?.labour) ? op.labour.length : 0;
        return acc + partsCount + labourCount;
      }, 0);
      this.cartItemsCount.set(totalCount);
      this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: totalCount, cartName: cartData?.cartName ?? this.titleValue() } });
    });
  }

  openOrder() {
    this.closeCart(this.menuTrigger);
    if (this.distributorLength() == 0) {
      return;
    }
    const link = `/${SRP_ROUTES.ORDER_PREPARATION.PATH}?cartId=${encodeURIComponent(String(this.cartId ?? ''))}`;
    this.reusableService.navigateToUrl(link, this.appContext, 'order-preparation');
  }
  onBlur(): void {
    this.editTitle.set(true);
  }

  closeCartTitle(): void {
    this.updateCartName();
    this.editTitle.set(true);
  }

  async getQuantityValue(value: number, row: any): Promise<void> {
    if (this.isFrozen()) return;
    const request = {
      cartId: this.cartId,
      cartName: this.cartTitle || this.titleValue() || 'Untitled Cart',
      itemId: row.itemId,
      qty: row.quantity,
    };

    const updateCartResult = await firstValueFrom(this.miniCartService.updateCart(request));
    const cartData = updateCartResult?.data?.updateProductsToCart?.cart
      ?? updateCartResult?.data?.data?.updateProductsToCart?.cart;

    if (!cartData || !Array.isArray(cartData.cartItems)) {
      console.warn('Mini-cart quantity update returned no cart data');
      return;
    }

    this.miniCartService.setCartData(cartData);
    // Derive count from update response and update mini-cart info
    const totalCount = (cartData?.cartItems ?? []).reduce((acc: number, op: any) => {
      const partsCount = Array.isArray(op?.parts) ? op.parts.length : 0;
      const labourCount = Array.isArray(op?.labour) ? op.labour.length : 0;
      return acc + partsCount + labourCount;
    }, 0);
    this.cartItemsCount.set(totalCount);
    this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: totalCount, cartName: cartData?.cartName } });
    this.groupOperations(cartData);
    this.expandedOp = row.opId;
  }

  // async updateCountState(): Promise<void> {
  //   // Prefer deriving count from in-memory cart data; fallback to API
  //   try {
  //     const cartSubject = this.miniCartService.getCartData?.();
  //     const cartValue = cartSubject?.getValue?.() ?? cartSubject ?? null;
  //     const cartObj = cartValue?.cart ?? cartValue;
  //     if (cartObj?.cartItems) {
  //       const totalCount = (cartObj.cartItems ?? []).reduce((acc: number, op: any) => {
  //         const partsCount = Array.isArray(op?.parts) ? op.parts.length : 0;
  //         const labourCount = Array.isArray(op?.labour) ? op.labour.length : 0;
  //         return acc + partsCount + labourCount;
  //       }, 0);
  //       this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: totalCount, cartName: cartObj?.cartName } });
  //       return;
  //     } else {
  //       const resp = await this.miniCartService.getCartItemsCount(this.cartId, true);
  //       this.miniCartService.updateMiniCartInfo(resp?.data);
  //     }
  //   } catch (e) {
  //     console.error('Error deriving cart count from cache, falling back to API', e);
  //   }

  // }

  // async getCartItemsCount(cartId: string) {
    // Try to derive from cached cart data first
    // try {
    //   const cartSubject = this.miniCartService.getCartData?.();
    //   const cartValue = cartSubject?.getValue?.() ?? cartSubject ?? null;
    //   const cartObj = cartValue?.cart ?? cartValue;
    //   if (cartObj?.cartItems) {
    //     const totalCount = (cartObj.cartItems ?? []).reduce((acc: number, op: any) => {
    //       const partsCount = Array.isArray(op?.parts) ? op.parts.length : 0;
    //       const labourCount = Array.isArray(op?.labour) ? op.labour.length : 0;
    //       return acc + partsCount + labourCount;
    //     }, 0);
    //     this.cartItemsCount.set(totalCount);
    //     const cartName = cartObj?.cartName;
    //     this.titleValue.set(cartName || ((this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART)) || 'Untitled Cart'));
    //     return;
    //   }
    // } catch (e) {
    //   console.error('Error deriving cart count from cache, falling back to API', e);
    // }

  //   this.miniCartService.getCartItemsCount(cartId, true)
  //     .then((resp) => {
  //       if (resp?.data) {
  //         this.cartItemsCount.set(resp?.data?.cartItemCount?.count);
  //         const cartName = resp?.data?.cartItemCount?.cartName;
  //         this.titleValue.set(cartName || ((this.contentPipe.transform(this.pageContentLabels, this.LABELS.UNTITLED_CART)) || 'Untitled Cart'));
  //       }

  //     })
  //     .catch((error) => {
  //       console.error('Error fetching count', error);
  //     })
  // }

  onOverlayResponse(confirmed: boolean) {
    if (confirmed && this.selectedRow && this.selectedType) {
      this.doRemoveOperation();
    }
  }
  onOverlayResponseOperation(confirmed: boolean, row: any) {
    if (confirmed && this.selectedRow && this.selectedType) {
      this.removeAccordion(row);
    }
  }
  doRemoveOperation() {
    this.deleteRowCart(this.selectedRow, this.selectedType);
  }

  onRemoveClicked(row: any, type: CartItemType, index: number, event: Event) {
    if (this.isFrozen()) return;
    event.stopPropagation();
    this.selectedRow = row;
    this.selectedIndex = index;
    this.selectedType = type;
    this.expandedOp = row.opId;

    if (!this.getShowRemovalConfirmation()) {
      this.performRemove(type, true, index);
      return;
    }

    let heading = '';
    let description = '';
    let button_one = this.cancel();
    let button_two = this.remove();
    if (type === 'operation') {
      heading = this.overlayHeadingOperation();
      description = this.overlayDescriptionOperation();
    } else if (type === 'parts') {
      heading = this.overlayHeadingParts();
      description = this.overlayDescriptionParts();
    } else if (type === 'labour') {
      heading = this.overlayHeadingLabour();
      description = this.overlayDescriptionLabour();
    }

    const dialogData: RemoveConfirmationDialogData = {
      heading,
      description,
      button_one,
      button_two,
      // Add skip label from label system using content filter pipe
      skipLabel: this.contentPipe.transform(this.pageContentLabels, this.LABELS.SKIP_NEXT_TIME) || 'Skip Next Time'
    };

    const dialogRef = this.dialog.open(RemoveConfirmationDialog, {
      panelClass: 'remove-confirmation-dialog-container',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.performRemove(type, confirmed, index);
      }
    });
  }

  private performRemove(type: string, confirmed: boolean, index: number) {
    if (type === 'operation') {
      this.onOverlayResponseOperation(confirmed, index);
    } else {
      this.onOverlayResponse(confirmed);
    }
  }

  private getShowRemovalConfirmation(): boolean {
    const userDetails = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_DETAILS) as UserDetailsPartial;
    return userDetails?.showRemovalConfirmation || false;
  }

  async deleteRowCart(row: any, type: CartItemType) {
    if (!row || !type) return;

    const itemId = type === 'parts'
      ? row.itemId || row.partsReference || row.sku
      : row.itemId || row.labourCode || row.code;

    const payload = {
      cartId: this.cartId,
      partItems: type === 'parts' ? [{ itemId: itemId.toString() }] : [],
      labourItems: type === 'labour' ? [{ itemId: itemId.toString() }] : []
    };

    const updateCartResult = await firstValueFrom(this.miniCartService.deleteCartItemApi(payload));
    const cartData = updateCartResult.data?.data?.deleteProductsFromCart?.cart;
    const { cartName, cartItemsCount } = cartData;

    this.miniCartService.setCartData(cartData);
    
    this.groupOperations(updateCartResult.data.data.deleteProductsFromCart.cart);

    this.expandedOp = row.opId;
    if (cartData.cartItems.length === 0) {
      this.closeCart(this.menuTrigger);
      this.miniCartService.setCartData(null);
      this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: 0, cartName: cartData.cartName } });
    } else {
      this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: cartItemsCount, cartName } });
    }
  }

  // Group parts and labour by operation
  groupOperations(cart: any) {
    this.totalPrice = 0;
    const operationGroupsMap: Record<string, { parts: any[]; labour: any[] }> = {};

    // Group items by operation
    cart.cartItems.forEach((item: any) => {
      item.parts.forEach((part: any) => {
        const op = part.operation;
        if (op != null) {
          if (!operationGroupsMap[op]) {
            operationGroupsMap[op] = { parts: [], labour: [] };
          }
          operationGroupsMap[op].parts.push(part);
        }

      });

      item.labour.forEach((lab: any) => {
        const op = lab.operation;
        if (op != null) {
          if (!operationGroupsMap[op]) {
            operationGroupsMap[op] = { parts: [], labour: [] };
          }
          operationGroupsMap[op].labour.push(lab);
        }
      });
    });

    this.operationGroups = [];

    Object.entries(operationGroupsMap).forEach(([operationName, group], index) => {
      const partsTableKey = `minicart-parts-${operationName}`;
      const labourTableKey = `minicart-labour-${operationName}`;

      let subTotal = 0;

      // Configure parts table
      const partsColumns: any[] = [
        { name: 'description', display: (this.contentPipe.transform(this.labels, this.LABELS.PARTS_DESCRIPTION)) || 'Part Description', template: this.descriptionTemplate },
        { name: 'quantity', display: (this.contentPipe.transform(this.labels, this.LABELS.QUANTITY)) || 'Quantity', template: this.quantityTemplate },
        { name: 'availability', display: (this.contentPipe.transform(this.labels, this.LABELS.AVAILABILITY)) || 'Availability', template: this.availabilityTemplate },
        { name: 'totalPriceIncludingVAT', display: (this.contentPipe.transform(this.labels, this.LABELS.TOTAL_PRICE_INCL_VAT)) || 'Total Price Incl. VAT', template: this.priceTemplate }
      ];
      if (!this.isFrozen()) {
        partsColumns.push({ name: 'Action', display: (this.contentPipe.transform(this.labels, this.LABELS.ACTIONS)) || 'Actions', template: this.actionTemplate });
      }
      this.tableService.setColumnsDataKey(partsTableKey, partsColumns);

      const mappedParts = group.parts.map((part: any) => {
        const rawPriceExcl = Number(
          part?.unitPriceExcludingVat ??
          0
        );
        const rawPriceIncl = Number(
          part?.unitPriceIncludingVat ??
          0
        );

        const discountPct = parseFloat(String(part?.discountPercentage ?? '0').replace(/%/g, '')) || 0;
        const discountFactor = 1 - discountPct / 100;
        const qty = Number(part?.quantity ?? 1);

        const calculatedTotalExclVat = rawPriceExcl * qty * discountFactor;
        const calculatedTotalInclVat = rawPriceIncl * qty * discountFactor;
        const totalExclVat = Number(part?.totalPriceExcludingVat ?? calculatedTotalExclVat);
        const totalInclVat = Number(part?.totalPriceIncludingVat ?? calculatedTotalInclVat);

        subTotal += Number(totalInclVat);

        const cPart = {
          itemId: part.itemId,
          description: {
            mainText: part.description,
            subText: part.sku
          },
          quantity: part.quantity,
          availability: {
            mainText: 0,
            subText: ''
          },
          discount: (part?.isStockPrixDataAvailable?.toLowerCase() === 'true') ? `${discountPct.toFixed(2)} %` : '-',
          totalPriceIncludingVAT: `${Number(totalInclVat).toFixed(2)} ${this.currencySymbol}`,
          totalPriceExcludingVAT: `${Number(totalExclVat).toFixed(2)} ${this.currencySymbol}`,
          Action: 'delete',
          operation: part.operation,
          opId: index,
          type: 'parts'
        };
        if (part.localStock) {
          cPart.availability.mainText = part.localStock;
          cPart.availability.subText = 'Local';
        } else if (part.relayStock) {
          cPart.availability.mainText = part.relayStock;
          cPart.availability.subText = 'Relay';
        } else if (part.distStock) {
          cPart.availability.mainText = part.distStock;
          cPart.availability.subText = 'Distrigo';
        }
        return cPart;
      });

      this.tableService.setTableDataKey(partsTableKey, mappedParts);

      // Configure labour table
      const labourColumns: any[] = [
        { name: 'description', display: (this.contentPipe.transform(this.labels, this.LABELS.LABOUR_DESCRIPTION)) || 'Labour Description', template: this.descriptionTemplate },
        { name: 'hours', display: (this.contentPipe.transform(this.labels, this.LABELS.LABOUR_TIME)) || 'Labour Time' },
        { name: 'rate', display: (this.contentPipe.transform(this.labels, this.LABELS.RATE)) || 'Rate' },
        { name: 'total', display: (this.contentPipe.transform(this.labels, this.LABELS.TOTAL_PRICE_INCL_VAT)) || 'Total Price Incl. VAT' }
      ];
      if (!this.isFrozen()) {
        labourColumns.push({ name: 'Action', display: (this.contentPipe.transform(this.labels, this.LABELS.ACTIONS)) || '', template: this.actionTemplateLabour });
      }
      this.tableService.setColumnsDataKey(labourTableKey, labourColumns);

      // Remove vat after IL sends data
      const labourVat = this.cacheService.getFromCache(CACHE_KEYS.USER.USER_DETAILS)?.vat;
      const mappedLabour = group.labour.map((lab: any) => {
        const hourlyRate = Number(lab?.hourlyRate ?? 0);
        const labourTime = Number(lab?.time ?? 0);
        const totalExcl = Number(lab?.priceExcludingtax ?? (hourlyRate * labourTime));
        const totalIncl = Number(lab?.price ?? (totalExcl * (1 + (labourVat ?? 0) / 100)));
        subTotal += Number(totalIncl);
        return {
          itemId: lab.itemId,
          description: {
            mainText: lab.description,
            subText: lab.sku
          },
          hours: lab.time,
          rate: lab.technicity,
          total: `${(Number(totalIncl)).toFixed(2)} ${this.currencySymbol}`,
          Action: 'delete',
          opId: index,
          type: 'labor'
        };
      });

      const sub = Number(subTotal);
      if (!isNaN(sub)) {
        const roundedSub = Math.round(sub * 100) / 100;
        const current = Number(this.totalPrice ?? 0);
        const total = Math.round((current + roundedSub) * 100) / 100
        this.totalPrice = total;
      }
      this.tableService.setTableDataKey(labourTableKey, mappedLabour);
      // Push operation group with its own subtotal
      this.operationGroups.push({
        operationName,
        partsTableKey,
        labourTableKey,
        subTotal: (Number(subTotal)).toFixed(2),
        totalPrice: this.totalPrice,
        partsLength: group.parts.length,
        labourLength: group.labour.length
      });
    });
  }

  async removeAccordion(index: number): Promise<void> {
    const accordion = this.operationGroups[index];
    this.miniCartService.getCart(this.cartId).then(async (cartData: any) => {
      if (cartData?.data) {
        const accordionItems = cartData.data?.getCartById?.cart?.cartItems?.filter((item: any) => {
          return item.operation === accordion.operationName;
        }) ?? [];

        if (accordionItems.length === 0) {
          console.warn('No accordion items found for operation:', accordion.operationName);
          return;
        }
        const { parts, labour } = accordionItems[0];

        const partsArray: any[] = [];
        const labourArray: any[] = [];
        parts.forEach((part: any) => {
          partsArray.push({ itemId: part.itemId.toString() });
        });
        labour.forEach((labour: any) => {
          labourArray.push({ itemId: labour.itemId.toString() });
        });

        const payload = {
          cartId: this.cartId,
          partItems: partsArray,
          labourItems: labourArray,
        };
        const updateCartResult = await firstValueFrom(this.miniCartService.deleteCartItemApi(payload));
        const cart = updateCartResult?.data?.data?.deleteProductsFromCart?.cart;
        const { cartName, cartItemsCount } = cart;
        this.miniCartService.setCartData(cart);
        
        this.groupOperations(cart);
        if (cart.cartItems.length === 0) {
          this.closeCart(this.menuTrigger);
          this.miniCartService.setCartData(null);
          this.miniCartService.updateMiniCartInfo({ cartItemCount: { count: 0, cartName: cart.cartName } });
        } else {
          this.miniCartService.updateMiniCartInfo({cartItemCount: {count: cartItemsCount, cartName: cartName}});
        }
      }


    });


  }
  closeCart(trigger?: MatMenuTrigger): void {
    const event = new CustomEvent('closeCart', { bubbles: true, composed: true });
    this.elRef.nativeElement.dispatchEvent(event);
    this.cartEventService.closeCart();
    const t = trigger ?? this.menuTrigger;
    try {
      t?.closeMenu?.();
    } catch (e) {
      console.warn('Failed to close menu via trigger', e);
    }
  }

  openCart(): void {
    this.closeCart(this.menuTrigger);

    const link = `/cart?id=${encodeURIComponent(String(this.cartId ?? ''))}`;

    this.reusableService.navigateToUrl(link, this.appContext, 'cart');
  }

  openEstimate(): void {
    this.openNewEstimate();
  }

  openNewEstimate(): void {
    if (this.estimateId) {
      const dialogData: RemoveConfirmationDialogData = {
        heading: this.contentPipe.transform(this.pageContentLabels, this.LABELS.CREATE_NEW_ESTIMATE_HEADING) || 'Create New Estimate?',
        description: this.contentPipe.transform(this.pageContentLabels, this.LABELS.CREATE_NEW_ESTIMATE_DESCRIPTION) || 'An estimate already exists. Do you want to create a new one?',
        button_one: this.contentPipe.transform(this.pageContentLabels, this.LABELS.CREATE_NEW_ESTIMATE_CANCEL) || 'Cancel',
        button_two: this.contentPipe.transform(this.pageContentLabels, this.LABELS.CREATE_NEW_ESTIMATE_CONFIRM) || 'OK',
        hideSkipOption: true
      };

      const dialogRef = this.dialog.open(RemoveConfirmationDialog, {
        panelClass: 'remove-confirmation-dialog-container',
        data: dialogData,
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.createEstimate();
        }
      });

      return;
    }

    this.createEstimate();
  }

  private createEstimate(): void {
    const rawCartData = this.miniCartService.getCartData().getValue();
    const cartData = {
      ...rawCartData,
      cartId: this.cartId,
      cartMaskId: this.cartId,
      createdDate: rawCartData?.createdDate,
      cartItems: (rawCartData?.cartItems ?? []).map((item: any) => ({
        ...item,
        parts: (item.parts ?? []).map((part: any) => ({
          ...part,
          discount: String(parseFloat(String(part.discountPercentage ?? '0').replace(/%/g, '')) || 0),
        })),
        labour: (item.labour ?? []).map((labour: any) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { priceExcludingtax, ...rest } = labour;
          return rest;
        }),
      })),
    };
    this.estimateListService.getEstimatesCart(cartData).subscribe({
      next: (resp: any) => {
        if (resp.success) {
          const estimateId = resp.data.data.updateEstimate.estimateId;
          this.estimateId = estimateId;
          this.closeCart(this.menuTrigger);

          const link = `/${SRP_ROUTES.ESTIMATES.PATH}?estimateId=${encodeURIComponent(String(estimateId ?? ''))}`;
          this.reusableService.navigateToUrl(link, this.appContext, 'estimate-details', {
            state: { from: 'cart' }
          });
        }
      }
    });
  }

  private cloneEstimate(): void {
    if (!this.estimateId) {
      return;
    }

    this.estimateService.cloneEstimate(this.estimateId).subscribe({
      next: (res: any) => {
        const newEstimateId = res?.data?.cloneEstimate?.estimateId;

        if (!newEstimateId) {
          return;
        }

        this.estimateId = newEstimateId;
        this.closeCart(this.menuTrigger);

        const link = `/${SRP_ROUTES.ESTIMATES.PATH}?estimateId=${encodeURIComponent(String(newEstimateId))}`;
        this.reusableService.navigateToUrl(link, this.appContext, 'estimate-details', {
          state: { from: 'cart' }
        });

        this.srpSnackbarService.show({
          message: 'Estimate cloned successfully',
          type: 'success',
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
      error: (err) => {
        console.error('cloneEstimate failed:', err);

        this.srpSnackbarService.show({
          message: 'Failed to clone estimate. Please try again.',
          type: 'warning',
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}